import { PencilSimple, TrashSimple } from '@phosphor-icons/react'
import { Button } from 'antd'
import Spinner from 'components/Spinner'
import CreateEditAutomationModal from 'pages/Settings/Automations/components/CreateEditAutomationModal'
import { useDeleteWorkflows } from 'queries/automations/useDeleteWorkflows'
import { useLoadWorkflows } from 'queries/automations/useLoadWorkflows'
import * as React from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import type { Workflow } from 'types/automations'
import Badge from 'ui/atoms/Badge'
import Flex from 'ui/atoms/Flex'
import Space from 'ui/atoms/Space'
import Table from 'ui/atoms/Table'
import SearchBar from 'ui/SearchBar'
import TableEmptyMessage from 'ui/table/TableEmptyMessage'
import Theme from 'ui/theme'
import { ZentakeContext } from 'utils/ZentakeContext'

import Tooltip from '@/ui/atoms/Tooltip'

const Wrapper = styled.div`
  background-color: white;
  padding: ${Theme.spacing.large};
  overflow: auto;
`

function getImmediateTriggerText(workflow: Workflow) {
  const triggerIsCheckedOut = workflow.trigger === 'CHECKED_OUT'

  if (triggerIsCheckedOut) {
    return 'Upon Check Out'
  }

  return 'Upon Scheduling'
}

function getScheduledTriggerText(workflow: Workflow) {
  const triggerIsCheckedOut = workflow.trigger === 'CHECKED_OUT'

  return `${
    triggerIsCheckedOut ? workflow.send_after : workflow.send_before
  } Day(s) ${triggerIsCheckedOut ? 'After' : 'Before'}`
}

function getTriggerText(workflow: Workflow) {
  if (workflow.send_upon_scheduling) {
    return getImmediateTriggerText(workflow)
  }

  return getScheduledTriggerText(workflow)
}

function Automations() {
  const { data: calendarWorkflows, isLoading: isListCalendarLoading } =
    useLoadWorkflows()
  const { deleteWorkflow, isLoading: isDeleteCalendarLoading } =
    useDeleteWorkflows()
  const calendarWorkflowsLoading =
    isListCalendarLoading || isDeleteCalendarLoading

  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [selectedWorkflow, setSelectedWorkflow] =
    React.useState<Workflow | null>(null)
  const [search, setSearch] = React.useState('')
  const [filteredWorkflows, setFilteredWorkflows] = React.useState<Workflow[]>(
    []
  )

  const { setAlertConfig } = React.useContext(ZentakeContext)

  React.useEffect(() => {
    if (search) {
      const filtered =
        calendarWorkflows?.filter((workflow) =>
          workflow.name.toLowerCase().includes(search.toLowerCase())
        ) ?? []
      setFilteredWorkflows(filtered)
    } else {
      setFilteredWorkflows(calendarWorkflows ?? [])
    }
  }, [search, calendarWorkflows])

  const showDeleteConfirmation = React.useCallback(
    async () =>
      new Promise((resolve) =>
        setAlertConfig({
          title: 'Are you sure you want to delete this Automation?',
          description:
            'Note: This will not delete any forms or packets associated with this Automation.',
          buttons: [
            {
              title: 'Delete',
              style: 'primary',
              danger: true,
              onPress: () => resolve(true),
            },
            {
              title: 'Cancel',
              onPress: () => resolve(false),
            },
          ],
          canDismiss: false,
        })
      ),
    [setAlertConfig]
  )

  const handleDelete = React.useCallback(
    async (workflowId) => {
      const result = await showDeleteConfirmation()
      if (result) {
        try {
          await deleteWorkflow({ id: workflowId })
          toast.success('Automation deleted successfully')
        } catch (error) {
          toast.error('Error deleting Automation')
        }
      }
    },
    [deleteWorkflow, showDeleteConfirmation]
  )

  const renderTable = React.useCallback(() => {
    return (
      <div className="overflow-y-auto">
        <Table>
          <Table.Head
            columns={[
              'Name',
              'Appointment(s)',
              'Form/Packet',
              'Trigger',
              'Status',
              '',
            ]}
          />
          <Table.Body>
            {filteredWorkflows.map((workflow) => (
              <Table.Row key={workflow.id}>
                <Table.Column $truncate style={{ maxWidth: 240 }}>
                  {workflow.name}
                </Table.Column>
                <Table.Column>
                  <div className="flex flex-wrap gap-1.5">
                    {workflow.for_appointment_types.map((item) => (
                      <div
                        className="rounded-xl bg-gray-200/80 px-2 py-0.5 text-gray-600"
                        key={item.id}
                      >
                        {item.name}
                      </div>
                    ))}
                  </div>
                </Table.Column>
                <Table.Column>
                  {workflow.form_name ||
                    workflow.document_name ||
                    workflow.packet_name}
                </Table.Column>
                <Table.Column>{getTriggerText(workflow)}</Table.Column>
                <Table.Column>
                  <Badge
                    $success={workflow.enabled}
                    $warning={!workflow.enabled}
                    text={workflow.enabled ? 'Active' : 'Inactive'}
                  />
                </Table.Column>
                <Table.Column className="w-44 space-x-4">
                  <div className="flex w-36 justify-center gap-4">
                    <Tooltip mouseEnterDelay={0.35} title="Edit">
                      <Button
                        className="w-[fit-content] !px-4"
                        onClick={() => {
                          setSelectedWorkflow(workflow)
                          setIsModalOpen(true)
                        }}
                      >
                        <PencilSimple className="text-xl" />
                      </Button>
                    </Tooltip>
                    <Tooltip mouseEnterDelay={0.35} title="Delete">
                      <Button
                        className="w-[fit-content] !px-4"
                        onClick={() => handleDelete(workflow.id)}
                      >
                        <TrashSimple className="text-xl" />
                      </Button>
                    </Tooltip>
                  </div>
                </Table.Column>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    )
  }, [filteredWorkflows, handleDelete])

  const renderContent = React.useCallback(() => {
    if (calendarWorkflowsLoading) {
      return (
        <>
          <Spinner color="onLight" size={20} />
          <span className="ml-2">Loading...</span>
        </>
      )
    }

    if (!filteredWorkflows || filteredWorkflows.length === 0) {
      return <TableEmptyMessage>No Results Found</TableEmptyMessage>
    }

    return renderTable()
  }, [calendarWorkflowsLoading, filteredWorkflows, renderTable])

  return (
    <Wrapper>
      <Space>
        <Flex
          $flexDirection={{ default: 'col', lg: 'row' }}
          $horizontalSpacing={{ default: 'none', lg: 'large' }}
          $verticalSpacing={{ default: 'small', lg: 'none' }}
        >
          <SearchBar className="flex-1" onChange={setSearch} value={search} />
          <Button
            disabled={calendarWorkflowsLoading}
            onClick={() => {
              setIsModalOpen(true)
            }}
            type="primary"
          >
            Add Automation
          </Button>
        </Flex>
        {renderContent()}
        {isModalOpen && (
          <CreateEditAutomationModal
            calendarWorkflow={selectedWorkflow}
            onClose={() => {
              setIsModalOpen(false)
              setSelectedWorkflow(null)
            }}
          />
        )}
      </Space>
    </Wrapper>
  )
}

export default Automations
